import { useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, Ellipsis } from "lucide-react";

import { IconButton } from "@/components/ui/IconButton";
import { Menu, MenuItem, MenuLabel } from "@/components/ui/Menu";
import { useStored } from "@/hooks/useStored";
import { cn } from "@/lib/cn";

import { dropAt, mergeOrder, sameOrder, step } from "./order";

export interface Block {
  /** Stable, because it is what somebody's stored order is written in. */
  id: string;
  /** What the block is called in its menu and when a move is read out. */
  label: string;
  /**
   * False while the block has nothing to show. It keeps its place in the
   * order, so it comes back where the reader left it.
   */
  shown?: boolean;
  /**
   * A block with no panel heading to carry a grip, such as a row of figures.
   * It gets a small handle of its own, drawn over its top edge on hover.
   */
  bare?: boolean;
  /**
   * Draws the block. It is handed the block's move menu to put in its panel's
   * heading, and passes `grip` to that panel itself. A bare block is handed
   * nothing, because its handle carries the menu.
   */
  render: (menu: ReactNode) => ReactNode;
}

export interface BlocksProps {
  /** The blocks in the order the screen ships with. */
  blocks: Block[];
  /** Where this person's order is kept in their browser. */
  storageKey: string;
}

/** Anything that picks a block up: a panel's grip, or a bare block's handle. */
const HANDLE = ".u-grip, [data-grip]";

/**
 * A column of blocks that the reader can put in their own order.
 *
 * Dragging is by the grip, so that text inside a block can still be selected
 * and a link can still be dragged to the tab bar: the block is only
 * draggable for as long as the pointer is down on its handle. A line in the
 * accent colour shows the gap the block will land in.
 *
 * Dragging leaves out keyboards and phones, so every block also has a menu
 * with Move up and Move down, and that is the whole feature for somebody who
 * cannot drag. The order is this person's own, kept in their browser; nobody
 * else's screen changes.
 *
 * None of it prints. A panel's grip belongs to the panel, which this cannot
 * mark, so it is hidden on paper from here.
 */
export function Blocks({ blocks, storageKey }: BlocksProps) {
  const [stored, setStored] = useStored<string[]>(storageKey, []);
  // The block whose handle the pointer is down on, the block in the air, and
  // the gap it would land in.
  const [armed, setArmed] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [slot, setSlot] = useState<number | null>(null);
  // The last move, for somebody who cannot see the block change place.
  const [said, setSaid] = useState("");

  const ids = blocks.map((block) => block.id);
  const shown = blocks.filter((block) => block.shown !== false).map((block) => block.id);
  const order = mergeOrder(stored, ids);
  const visible = order.filter((id) => shown.includes(id));
  const changed = !sameOrder(visible, shown);

  function say(block: Block, next: string[], how: string) {
    const position = next.filter((id) => shown.includes(id)).indexOf(block.id) + 1;
    setSaid(`${block.label} ${how}, now ${position} of ${visible.length}.`);
  }

  function move(block: Block, by: -1 | 1) {
    const next = step(order, block.id, by, shown);
    setStored(next);
    say(block, next, by === -1 ? "moved up" : "moved down");
  }

  function settle() {
    setArmed(null);
    setDragging(null);
    setSlot(null);
  }

  function land() {
    const block = blocks.find((each) => each.id === dragging);
    const next = block && slot !== null ? dropAt(order, block.id, slot, shown) : null;
    if (block && next) {
      setStored(next);
      say(block, next, "moved");
    }
    settle();
  }

  return (
    <div
      className="space-y-5"
      // The gaps between blocks are part of the column, not of any block, and
      // a drop into one has to be allowed or the block springs back.
      onDragOver={(event) => {
        if (dragging) event.preventDefault();
      }}
      onDrop={(event) => {
        if (!dragging) return;
        event.preventDefault();
        land();
      }}
    >
      {visible.map((id, index) => {
        const block = blocks.find((each) => each.id === id)!;
        const menu = (
          <BlockMenu
            label={block.label}
            canMoveUp={index > 0}
            canMoveDown={index < visible.length - 1}
            onMove={(by) => move(block, by)}
            quiet={!block.bare}
          />
        );
        return (
          <div
            key={id}
            draggable={armed === id ? true : undefined}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              if (event.target instanceof Element && event.target.closest(HANDLE)) setArmed(id);
            }}
            onPointerUp={() => setArmed(null)}
            onDragStart={(event) => {
              // A link or a picture inside a block is draggable in its own
              // right, and its drag bubbles through here. That one is not ours.
              if (event.target !== event.currentTarget) return;
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", block.label);
              setDragging(id);
            }}
            onDragEnd={settle}
            onDragOver={(event) => {
              if (!dragging) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              // The upper half of a block is the gap above it, the lower half
              // the gap below.
              const box = event.currentTarget.getBoundingClientRect();
              const gap = event.clientY < box.top + box.height / 2 ? index : index + 1;
              setSlot(dropAt(order, dragging, gap, shown) ? gap : null);
            }}
            className={cn(
              "group/block relative",
              // The panel's grip is the handle. It is eight pixels wide, so it
              // is given a larger area to be caught by, and it is not for paper.
              "[&_.u-grip]:relative [&_.u-grip]:cursor-grab [&_.u-grip]:after:absolute [&_.u-grip]:after:-inset-2.5 [&_.u-grip]:after:content-['']",
              "print:[&_.u-grip]:hidden",
              dragging === id && "opacity-50",
            )}
          >
            {slot === index ? <LandingLine className="-top-[11px]" /> : null}
            {block.bare ? (
              <div
                data-print="hide"
                className={cn(
                  "absolute -top-4 left-3 z-10 flex h-8 items-center rounded-sm bg-surface-raised pl-1 shadow-raised ring-1 ring-line",
                  // Out of the way until the block is pointed at or tabbed
                  // into. A finger cannot point, so there it is always shown.
                  "opacity-0 focus-within:opacity-100 group-hover/block:opacity-100 has-[[aria-expanded=true]]:opacity-100 pointer-coarse:opacity-100",
                )}
              >
                <span
                  data-grip
                  aria-hidden="true"
                  className="grid h-8 w-5 cursor-grab place-items-center active:cursor-grabbing"
                >
                  <span className="u-grip" />
                </span>
                {menu}
              </div>
            ) : null}
            {block.render(block.bare ? null : menu)}
            {slot === visible.length && index === visible.length - 1 ? (
              <LandingLine className="-bottom-[11px]" />
            ) : null}
          </div>
        );
      })}

      {changed ? (
        <div data-print="hide" className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setStored([]);
              setSaid("The layout is back to how it started.");
            }}
            className="rounded-xs px-1.5 py-1 text-xs font-medium text-fg-subtle transition-colors hover:bg-ink-100 hover:text-fg"
          >
            Reset layout
          </button>
        </div>
      ) : null}

      <p data-print="hide" className="sr-only" role="status" aria-live="polite">
        {said}
      </p>
    </div>
  );
}

/** Where the block in the air will come down: a line across the gap. */
function LandingLine({ className }: { className: string }) {
  return (
    <span
      data-print="hide"
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-x-0 z-10 h-0.5 rounded-full bg-accent", className)}
    />
  );
}

/**
 * Move up and Move down, for a block.
 *
 * The menu is headed with the block's name, which matters for two panels that
 * move as a pair: it says what is about to move before anything does.
 */
function BlockMenu({
  label,
  canMoveUp,
  canMoveDown,
  onMove,
  quiet,
}: {
  label: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (by: -1 | 1) => void;
  /** In a panel's heading, where it waits for the block to be pointed at. */
  quiet: boolean;
}) {
  const trigger = useRef<HTMLButtonElement>(null);

  function move(by: -1 | 1) {
    onMove(by);
    // Moving a block lifts it out of the page and puts it back, and the
    // button that was focused goes with it and comes back unfocused. Somebody
    // moving a block three places by keyboard should not have to find it
    // again each time.
    requestAnimationFrame(() => trigger.current?.focus());
  }

  return (
    <Menu
      align="end"
      trigger={
        <IconButton
          ref={trigger}
          size="sm"
          label={`Move section: ${label}`}
          icon={<Ellipsis className="size-4" />}
          data-print="hide"
          className={
            quiet
              ? "opacity-0 focus-visible:opacity-100 group-hover/block:opacity-100 aria-expanded:opacity-100 pointer-coarse:opacity-100"
              : undefined
          }
        />
      }
    >
      <MenuLabel>{label}</MenuLabel>
      <MenuItem
        icon={<ArrowUp className="size-4" />}
        disabled={!canMoveUp}
        onSelect={() => move(-1)}
      >
        Move up
      </MenuItem>
      <MenuItem
        icon={<ArrowDown className="size-4" />}
        disabled={!canMoveDown}
        onSelect={() => move(1)}
      >
        Move down
      </MenuItem>
    </Menu>
  );
}
